<template>
  <div class="galeria-fotos-acao-container">
    <!-- Cabeçalho da Seção -->
    <div class="galeria-secao-header">
      <div class="header-titulo-area">
        <label class="secao-label">
          <i class="ti ti-photo"></i>
          Imagens da Ação
        </label>
        <span v-if="!isLoading && totalElementos > 0" class="badge-qtd-fotos">
          {{ totalElementos }} {{ totalElementos === 1 ? "foto" : "fotos" }}
        </span>
      </div>

      <button v-if="coordenador" type="button" class="btn-gerenciar-fotos" @click="abrirModal"
        title="Adicionar ou gerenciar fotos desta ação">
        <i class="ti ti-photo-plus"></i>
        <span>{{ totalElementos > 0 ? "Gerenciar Fotos" : "+ Adicionar Fotos" }}</span>
      </button>

      <button v-else-if="totalElementos > 0" type="button" class="btn-ver-todas" @click="abrirModal"
        title="Ver todas as fotos">
        <span>Ver todas ({{ totalElementos }})</span>
        <i class="ti ti-arrow-right"></i>
      </button>
    </div>

    <!-- Estado de Carregamento -->
    <div v-if="isLoading" class="galeria-carregando">
      <span class="spinner-carregando"></span>
      <span>Carregando fotos...</span>
    </div>

    <!-- Estado Vazio -->
    <div v-else-if="imagens.length === 0" class="galeria-vazia-card">
      <div class="vazia-icone">
        <i class="ti ti-camera"></i>
      </div>
      <div class="vazia-conteudo">
        <p class="vazia-titulo">Nenhuma foto cadastrada</p>
        <p class="vazia-subtitulo">
          {{
            coordenador
              ? "Adicione registros visuais das atividades, eventos e oficinas desta ação."
              : "Esta ação ainda não possui registros fotográficos adicionados."
          }}
        </p>
      </div>
      <button v-if="coordenador" type="button" class="btn-adicionar-vazio" @click="abrirModal">
        <i class="ti ti-plus"></i> Adicionar Foto
      </button>
    </div>

    <!-- Faixa de Miniaturas (Strip de Fotos) -->
    <div v-else class="galeria-strip-wrapper">
      <div class="galeria-strip-imagens">
        <div v-for="(foto, index) in imagens.slice(0, 4)" :key="foto.id || foto.acaoImagemId || index"
          class="foto-strip-card" :title="foto.descricao || 'Foto da ação'" @click="abrirLightbox(foto)">
          <img :src="resolverUrlFoto(foto)" :alt="foto.descricao || 'Foto da ação'" loading="lazy" />
          <div class="foto-strip-overlay">
            <i class="ti ti-zoom-in"></i>
          </div>

          <!-- Indicador de +X fotos no último item da miniatura -->
          <div v-if="index === 3 && totalElementos > 4" class="foto-strip-mais-badge">
            <span>+{{ totalElementos - 4 }}</span>
          </div>
        </div>
      </div>

      <div class="galeria-strip-rodape">
        <span class="strip-legenda">
          <i class="ti ti-info-circle"></i> Clique em qualquer foto para abrir a galeria completa
        </span>
        <button type="button" class="btn-link-galeria" @click="abrirModal">
          {{ coordenador ? "Gerenciar Fotos" : "Ver Todas" }} &rarr;
        </button>
      </div>
    </div>

    <!-- Modal Completo de Fotos da Ação (Reutilizando ModalFotosEncontro) -->
    <ModalFotosEncontro :visivel="modalVisivel" :acao-id="acaoId" :titulo-acao="tituloAcao" :coordenador="coordenador"
      tipo="acao" @fechar="fecharModal" @atualizado="onFotosAtualizadas" />

    <GaleriaLightbox v-if="lightBoxVisivel" :foto="fotoLightBox" :resolver-url="resolverUrlFoto"
      :obter-data="(f) => (f.dataCriacao || f.criadoEm ? formatarDataHora(f.dataCriacao || f.criadoEm) : '')"
      :pode-remover="coordenador" @fechar="fecharLightbox" @remover="removerDoLightbox"
      @alterar-visibilidade="alterarVisibilidadeLightbox" />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useAcaoImagem } from "@/composables/useAcaoImagem";
import ModalFotosEncontro from "@/components/encontros/ModalFotosEncontro.vue";
import GaleriaLightbox from "../common/GaleriaLightbox.vue";

const props = defineProps({
  acaoId: {
    type: [String, Number],
    required: true,
  },
  tituloAcao: {
    type: String,
    default: "",
  },
  coordenador: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["atualizado"]);

const modalVisivel = ref(false);
const lightBoxVisivel = ref(false);
const fotoLightBox = ref(null)

const {
  imagens,
  isLoading,
  pagination,
  fetchImagensByAcao,
  deleteImagem
} = useAcaoImagem();

const totalElementos = computed(() => pagination.totalElements.value);

function resolverUrlFoto(foto) {
  if (!foto) return "";
  if (foto.imagemUrl) return foto.imagemUrl;
  if (foto.url) return foto.url;
  if (foto.imagem?.url) return foto.imagem.url;
  if (foto.imagem?.base64Data) {
    return `data:${foto.imagem.mimeType || "image/jpeg"};base64,${foto.imagem.base64Data}`;
  }
  return "";
}

async function carregarFotos() {
  if (!props.acaoId || props.acaoId === "undefined") {
    imagens.value = [];
    pagination.resetPagination();
    return;
  }
  await fetchImagensByAcao(0, 10, props.acaoId);
}

function abrirModal() {
  modalVisivel.value = true;
}

function fecharModal() {
  modalVisivel.value = false;
}

function abrirLightbox(foto) {
  lightBoxVisivel.value = true
  fotoLightBox.value = foto
}

function fecharLightbox() {
  lightBoxVisivel.value = false
  fotoLightBox.value = null
}

async function onFotosAtualizadas(dados) {
  await carregarFotos();
  emit("atualizado", dados);
}

async function removerDoLightbox(foto) {
  const fotoId = foto.id || foto.acaoImagemId;
  const confirmar = window.confirm(
    `Deseja realmente remover esta foto?\n\n"${foto.descricao || "Sem descrição"}"`
  );
  if (!confirmar) return;

  try {
    await deleteImagem(fotoId);
    fecharLightbox();
    carregarFotos();
  } catch (err) {
    console.error("Erro ao remover foto:", err);
    alert("Erro ao remover a foto.");
  }
}

watch(
  () => props.acaoId,
  (novoId) => {
    if (novoId && novoId !== "undefined") {
      carregarFotos();
    }
  }
);

onMounted(() => {
  carregarFotos();
});
</script>

<style scoped>
.galeria-fotos-acao-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  margin-top: 4px;
}

/* Cabeçalho */
.galeria-secao-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.header-titulo-area {
  display: flex;
  align-items: center;
  gap: 8px;
}

.secao-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--cinza-medio, #64748b);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.secao-label i {
  font-size: 15px;
  color: var(--roxo-primario, #6f319e);
}

.badge-qtd-fotos {
  background-color: var(--roxo-superficie, hsl(258, 45%, 95%));
  color: var(--roxo-primario, #6f319e);
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 99px;
  line-height: 1.2;
}

.btn-gerenciar-fotos {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--branco, #ffffff);
  border: 1.5px solid var(--roxo-primario, #6f319e);
  color: var(--roxo-primario, #6f319e);
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  font-family: var(--font-base);
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
}

.btn-gerenciar-fotos:hover {
  background: var(--roxo-primario, #6f319e);
  color: var(--branco, #ffffff);
  box-shadow: 0 2px 6px rgba(111, 49, 158, 0.2);
}

.btn-ver-todas {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: none;
  color: var(--roxo-primario, #6f319e);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 6px;
  transition: all 0.2s;
}

.btn-ver-todas:hover {
  color: var(--roxo-medio, #532378);
  transform: translateX(2px);
}

/* Estado de Carregamento */
.galeria-carregando {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  background-color: var(--cinza-claro, #f8fafc);
  border: 1.5px solid var(--roxo-superficie, #e2e8f0);
  border-radius: 10px;
  color: var(--cinza-medio, #64748b);
  font-size: 13px;
}

.spinner-carregando {
  width: 18px;
  height: 18px;
  border: 2px solid var(--roxo-superficie, #e2e8f0);
  border-top-color: var(--roxo-primario, #6f319e);
  border-radius: 50%;
  animation: girar 0.8s linear infinite;
}

@keyframes girar {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

/* Estado Vazio */
.galeria-vazia-card {
  display: flex;
  align-items: center;
  gap: 14px;
  background-color: var(--cinza-claro, #fafafa);
  border: 1.5px dashed var(--roxo-superficie, #e2e8f0);
  border-radius: 10px;
  padding: 16px 18px;
  transition: border-color 0.2s;
}

.galeria-vazia-card:hover {
  border-color: var(--roxo-claro, #b794f4);
}

.vazia-icone {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background-color: var(--roxo-superficie, hsl(258, 45%, 95%));
  color: var(--roxo-primario, #6f319e);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  flex-shrink: 0;
}

.vazia-conteudo {
  flex: 1;
  min-width: 0;
}

.vazia-titulo {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: var(--cinza-escuro, #334155);
}

.vazia-subtitulo {
  margin: 2px 0 0;
  font-size: 12px;
  color: var(--cinza-medio, #64748b);
  line-height: 1.35;
}

.btn-adicionar-vazio {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background-color: var(--roxo-primario, #6f319e);
  color: var(--branco, #ffffff);
  border: none;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-adicionar-vazio:hover {
  background-color: var(--roxo-medio, #532378);
  box-shadow: 0 2px 6px rgba(111, 49, 158, 0.25);
  transform: translateY(-1px);
}

/* Faixa de Miniaturas (Strip) */
.galeria-strip-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: var(--cinza-claro, #fafafa);
  border: 1.5px solid var(--roxo-superficie, #e2e8f0);
  border-radius: 10px;
  padding: 12px;
  transition: border-color 0.2s;
}

.galeria-strip-wrapper:hover {
  border-color: var(--roxo-claro, #b794f4);
}

.galeria-strip-imagens {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  cursor: pointer;
}

.foto-strip-card {
  position: relative;
  aspect-ratio: 3 / 2;
  border-radius: 8px;
  overflow: hidden;
  background-color: var(--roxo-superficie, #e2e8f0);
  border: 1px solid var(--roxo-superficie, #e2e8f0);
  transition: all 0.2s ease;
}

.foto-strip-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.25s ease;
}

.foto-strip-card:hover img {
  transform: scale(1.08);
}

.foto-strip-overlay {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 1.1rem;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.foto-strip-card:hover .foto-strip-overlay {
  opacity: 1;
}

.foto-strip-mais-badge {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.72);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  backdrop-filter: blur(2px);
}

.galeria-strip-rodape {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 4px;
  font-size: 11px;
}

.strip-legenda {
  color: var(--cinza-medio, #64748b);
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.btn-link-galeria {
  background: transparent;
  border: none;
  color: var(--roxo-primario, #6f319e);
  font-weight: 600;
  cursor: pointer;
  padding: 2px 4px;
  transition: all 0.2s;
}

.btn-link-galeria:hover {
  color: var(--roxo-medio, #532378);
  text-decoration: underline;
}

@media (max-width: 600px) {
  .galeria-vazia-card {
    flex-direction: column;
    text-align: center;
  }

  .btn-adicionar-vazio {
    width: 100%;
    justify-content: center;
  }

  .galeria-strip-imagens {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
